// frontend/src/pages/PortalDashboard.jsx (Phase 19)
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePortalAuth } from "../context/PortalAuthContext";
import { useAuth } from "../context/AuthContext";
import { portalApi } from "../services/portalApi";
import "../styles/portal.css";

import StepUpAuthModal from "../components/StepUpAuthModal";
import TwoFactorSetupModal from "../components/TwoFactorSetupModal";
import UsageQuotaCard from "../components/UsageQuotaCard";

export default function PortalDashboard() {
  const { client, logout, refresh } = usePortalAuth();
  const { onLogout: onCentralLogout } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modales de sécurité
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [stepUpAction, setStepUpAction] = useState(null); // { type: 'generate' | 'revoke', keyId?: number }

  const load = useCallback(async () => {
    const { ok, data } = await portalApi("/api/portal/keys");
    if (ok) setKeys(data.keys || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Le `code` est propagé jusqu'à la modale : sans lui, un compte sans aucun
  // facteur verrait un champ « mot de passe » qu'il ne peut pas remplir.
  // Voir docs/PORTAIL_SESSIONS_ET_STEPUP.md.
  const handleConfirmedGenerate = async (payload, headers) => {
    const { ok, data } = await portalApi("/api/portal/keys", { method: "POST", body: payload, headers });
    if (ok) {
      setNewKey(data.api_key);
      setStepUpAction(null);
      load();
      return { ok: true };
    }
    return { error: data?.error || "Échec de création de la clé.", code: data?.code };
  };

  const handleConfirmedRevoke = async (payload, headers) => {
    if (!stepUpAction?.keyId) return { error: "Clé introuvable" };
    const { ok, data } = await portalApi(`/api/portal/keys/${stepUpAction.keyId}`, { method: "DELETE", body: payload, headers });
    if (ok) {
      setStepUpAction(null);
      load();
      return { ok: true };
    }
    return { error: data?.error || "Échec de révocation de la clé.", code: data?.code };
  };

  const doLogout = async () => {
    await logout(); // Portal logout
    await onCentralLogout(); // Central logout
    navigate("/login");
  };

  const premium = client?.has_notary_access;
  const totpEnabled = client?.totp_enabled;

  return (
    <div className="pt-root">
      {show2FASetup && (
        <TwoFactorSetupModal
          onClose={() => setShow2FASetup(false)}
          onSuccess={() => { refresh && refresh(); load(); }}
        />
      )}

      {stepUpAction && (
        <StepUpAuthModal
          title={stepUpAction.type === "generate" ? "Création de Clé API" : "Révocation de Clé API"}
          actionName={stepUpAction.type === "generate" ? "La génération d'une nouvelle clé d'accès" : "La révocation définitive de cette clé API"}
          totpEnabled={totpEnabled}
          onConfirm={stepUpAction.type === "generate" ? handleConfirmedGenerate : handleConfirmedRevoke}
          onCancel={() => setStepUpAction(null)}
          onEnrollerTotp={() => { setStepUpAction(null); setShow2FASetup(true); }}
        />
      )}

      <div className="pt-header">
        <div>
          <h1 className="pt-title">Tableau de bord</h1>
          <p className="pt-subtitle">{client?.email}</p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            className={`pt-btn pt-btn--sm ${totpEnabled ? "pt-btn--ghost" : "pt-btn--primary"}`}
            style={{ fontSize: "0.8rem", border: totpEnabled ? "1px solid #00ff94" : undefined }}
            onClick={() => setShow2FASetup(true)}
          >
            {totpEnabled ? "🛡️ 2FA Activé ✓" : "🔐 Activer 2FA (TOTP)"}
          </button>
          <span className={`pt-badge ${premium ? "pt-badge--premium" : "pt-badge--free"}`}>
            {premium ? "Premium · Notaire ✓" : "Free"}
          </span>
          <Link className="pt-btn pt-btn--ghost pt-btn--sm" to="/portal/pricing">Offres</Link>
          <button className="pt-btn pt-btn--ghost pt-btn--sm" onClick={doLogout}>Déconnexion</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        <UsageQuotaCard />
      </div>

      <div className="pt-card pt-card--wide">
        {newKey && (
          <div className="pt-keybanner">
            <div className="pt-keybanner__warn">
              ⚠️ Nouvelle clé — copie-la maintenant, elle ne sera plus affichée.
            </div>
            <div className="pt-keybox">
              <code>{newKey}</code>
              <button className="pt-btn pt-btn--sm pt-btn--primary"
                      onClick={() => navigator.clipboard.writeText(newKey)}>Copier</button>
            </div>
          </div>
        )}

        <div className="pt-toolbar">
          <button className="pt-btn pt-btn--primary pt-btn--sm" onClick={() => setStepUpAction({ type: "generate" })}>
            + Générer une clé
          </button>
        </div>

        {loading ? (
          <p className="pt-subtitle">Chargement…</p>
        ) : (
          <table className="pt-table">
            <thead>
              <tr>
                <th>Préfixe</th><th>Offre</th><th>Requêtes</th>
                <th>Créée</th><th>Statut</th><th></th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 && (
                <tr><td colSpan={6} className="pt-subtitle">Aucune clé.</td></tr>
              )}
              {keys.map((k) => (
                <tr key={k.id}>
                  <td><code>{k.key_prefix}…</code></td>
                  <td>{k.plan}</td>
                  <td>{k.calls_total}</td>
                  <td>{k.created_at ? String(k.created_at).slice(0, 10) : "—"}</td>
                  <td>{k.active ? "Active" : "Révoquée"}</td>
                  <td className="pt-row-actions">
                    {k.active && (
                      <button className="pt-btn pt-btn--danger" onClick={() => setStepUpAction({ type: "revoke", keyId: k.id })}>
                        Révoquer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
